import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { initAmplitude } from "src/analitycs";
import { initialLogEvent } from "src/analitycs/pagesEvents/transactions";
import { setPermissions, setUser } from "src/redux/reducers/user";
import { getDecodeToken } from "src/utils/authentication";

import { authInterceptor } from "./interceptor";
import { clearSessionState } from "./session";
import { rolesToPermissions } from "./permissions/roles";

// PTMQRVA-506: claim de roles que emite portal-modo-develop. El tenant nuevo
// NO inyecta `permissions[]` para el resource server compartido
// (https://status-page-api.com); derivamos los permissions del array de roles
// vía mapeo del PRD (src/auth/permissions/roles.ts).
//
// Retrocompatibilidad: el tenant viejo (play-digital.auth0.com) usado todavía
// en preprod / production NO emite el claim de roles pero SÍ emite
// `accessTokenDecoded.permissions`. Mantenemos la lectura del claim viejo
// como fallback hasta que preprod / production también migren al tenant
// portal-modo.
const ROLES_CLAIM = "https://modo-auth0/roles";

export const AuthInject = () => {
  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const lastSubRef = useRef<string | undefined>(undefined);

  const getUserPermissions = useCallback(async (getToken) => {
    const token = await getToken();
    const accessTokenDecoded: any = await getDecodeToken(token);

    const roles: string[] = accessTokenDecoded?.[ROLES_CLAIM] ?? [];
    const fromRoles = rolesToPermissions(roles);
    if (fromRoles.length > 0) return fromRoles;

    // Fallback al claim del tenant viejo (preprod / production)
    const legacyPermissions: string[] = accessTokenDecoded?.permissions ?? [];
    return legacyPermissions;
  }, []);

  const handlePermissions = useCallback(async (getAccessTokenSilently, dispatch) => {
    try {
      const permissions = await getUserPermissions(getAccessTokenSilently);
      dispatch(setPermissions(permissions));
    } catch (error) {
      console.error("Error al cargar los permisos del usuario", error);
    }
  }, [getUserPermissions]);

  useEffect(() => {
    const currentSub = isAuthenticated ? user?.sub : undefined;
    if (lastSubRef.current && lastSubRef.current !== currentSub) {
      clearSessionState({ dispatch, queryClient });
    }
    lastSubRef.current = currentSub;

    authInterceptor.setAuthGetter(getAccessTokenSilently, user, isAuthenticated);
    user && initAmplitude(user.email);
    if (!isUserLoggedIn && isAuthenticated) {
      initialLogEvent(user.email);
      setIsUserLoggedIn(true);
    }
    dispatch(setUser(user));
    handlePermissions(getAccessTokenSilently, dispatch);

    return () => authInterceptor.setAuthGetter(undefined, undefined, undefined);
  }, [getAccessTokenSilently, user, isAuthenticated, isUserLoggedIn, dispatch, handlePermissions, queryClient]);

  return null;
};
